import { useState } from 'react';
import { Box } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../core/authContext';
import { FEATURE_LANGUAGE_OVER_TIME } from '../../core/features';
import { Sidebar } from '../Home/Sidebar';
import { NAV_ROUTES } from '../Home/navRoutes';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { CompositeTrajectory } from '../IndividualPlan/CompositeTrajectory';
import { DEFAULT_RECLASS_CRITERIA } from '../IndividualPlan/derive';
import { DomainProfile } from '../IndividualPlan/DomainProfile';
import { getFlashlightData } from '../IndividualPlan/flashlight';
import { FlashlightBenchmarksCard } from '../IndividualPlan/FlashlightBenchmarksCard';
import { FocusByDomain } from '../IndividualPlan/FocusByDomain';
import { elTeacherName, gradeText, metaItems, schoolName } from '../IndividualPlan/headerData';
import { MetaStrip } from '../IndividualPlan/MetaStrip';
import { seedSavedPlan } from '../IndividualPlan/planCache';
import { PlanHeader } from '../IndividualPlan/PlanHeader';
import { PlanSnapshot } from '../IndividualPlan/PlanSnapshot';
import { ProficiencyCard } from '../IndividualPlan/ProficiencyCard';
import { ReclassCriteriaProvider } from '../IndividualPlan/reclassCriteriaContext';
import { StateAssessments } from '../IndividualPlan/StateAssessments';
import { COLORS } from '../IndividualPlan/tokens';
import { TopBar, type Crumb } from '../IndividualPlan/TopBar';
import { SampleBanner } from './SampleBanner';
import { serverSampleGateSources, useSampleGate, type SampleGateSources } from './sampleGate';
import {
  SAMPLE_STUDENT_ID,
  sampleAssignments,
  sampleBenchmarks,
  sampleSavedPlan,
  sampleStudent,
} from './samplePlanFixture';
import { PAGE_MAX_WIDTH } from '../../theme';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type Entry = { id: 'reports' | 'roster'; crumbs: Crumb[] };

const FROM_REPORTS: Entry = {
  id: 'reports',
  crumbs: [{ label: 'Reports', to: NAV_ROUTES.reports }, { label: 'Student Action Plan' }],
};
const FROM_ROSTER: Entry = {
  id: 'roster',
  crumbs: [{ label: 'Students', to: NAV_ROUTES.roster }, { label: 'Student Action Plan' }],
};

function entryFor(state: unknown): Entry {
  return (state as { from?: unknown } | null | undefined)?.from === 'roster' ? FROM_ROSTER : FROM_REPORTS;
}
const LATEST_YEAR = sampleStudent.years[sampleStudent.years.length - 1]?.year ?? null;

function shortDate(d: Date): string {
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(2)}`;
}

export function SamplePlanPage({
  gateSources = serverSampleGateSources,
}: {
  gateSources?: SampleGateSources;
}) {
  const { isSuperAdmin, isDistrictAdmin, hasFeature } = useAuth();
  const gate = useSampleGate({ isSuperAdmin, isDistrictAdmin }, gateSources);
  const navigate = useNavigate();
  const entry = entryFor(useLocation().state);
  useState(() => {
    seedSavedPlan(SAMPLE_STUDENT_ID, sampleSavedPlan(DEFAULT_RECLASS_CRITERIA));
    return true;
  });

  if (gate.plan === 'pending') return null;
  if (gate.plan !== 'absent') return <Navigate to={NAV_ROUTES[entry.id]} replace />;

  const flashlight = getFlashlightData(sampleBenchmarks, LATEST_YEAR, sampleAssignments);
  const teacher = elTeacherName(sampleStudent, null);
  const subline = [
    schoolName(sampleStudent, null),
    gradeText(sampleStudent, null),
    teacher ? `EL Teacher: ${teacher}` : '',
    `Generated ${shortDate(new Date())}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: COLORS.bg }}>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId={entry.id} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar crumbs={entry.crumbs} />
        <Box sx={{ flex: 1, px: { xs: 3, md: 5 }, py: { xs: 3, md: 4 } }}>
          <Box sx={{ maxWidth: PAGE_MAX_WIDTH, mx: 'auto' }}>
            <Box sx={{ mb: 4 }}>
              <SampleBanner
                kind="plan"
                admin={isSuperAdmin || isDistrictAdmin}
                sisConnected={gate.sisConnected}
                accessUploaded={gate.accessUploaded}
                onUploadAccess={() => navigate(NAV_ROUTES.data)}
              />
            </Box>
            <PlanHeader fullName={sampleStudent.fullName} subline={subline} showShare={false} />
            <Box sx={{ borderTop: `1px solid ${COLORS.hairline}` }} />
            <MetaStrip items={metaItems(sampleStudent, null)} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pb: 4 }}>
              <ReclassCriteriaProvider value={DEFAULT_RECLASS_CRITERIA}>
                <PlanSnapshot student={sampleStudent} studentId={SAMPLE_STUDENT_ID} year={LATEST_YEAR} />
                <FocusByDomain student={sampleStudent} studentId={SAMPLE_STUDENT_ID} />
                <ProficiencyCard student={sampleStudent} year={LATEST_YEAR} />
                <CompositeTrajectory student={sampleStudent} />
                <DomainProfile student={sampleStudent} flashlight={flashlight} />
                {hasFeature(FEATURE_LANGUAGE_OVER_TIME) && <FlashlightBenchmarksCard planYear={LATEST_YEAR != null ? `${LATEST_YEAR - 1}-${String(LATEST_YEAR).slice(2)}` : ''} source={{ kind: 'live', data: sampleBenchmarks, failed: false }} onOpenProfile={() => navigate(NAV_ROUTES.roster)} />}
                <StateAssessments />
              </ReclassCriteriaProvider>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
